/**
 * @file shop-attention.ts
 * @description «Требует внимания» в карточке магазина (phase19.28 этап А;
 * uiux-platform-shops §4; PL6, PL9, SB59, SB138): что не готово к
 * открытию сайта и какие адреса не работают.
 *
 * Готовность — реестром `READINESS_META` (ключ `readiness` → текст и тон):
 * новое условие = запись, не ветка. Жёсткие условия PL9 (реквизиты,
 * политика ПДн) — «danger», остальное — «warning». Настраивает это
 * владелец в своём кабинете, поэтому строки ведут на вкладку «Доступ», где
 * есть вход в кабинет; адреса — на вкладку «Домены».
 */

import type { AttentionItem, AttentionTone } from "../../components/ui/AttentionList";
import type { ShopCard, ShopReadiness } from "../api-platform-shops";
import { checkStatusMeta } from "./shop-domain-status";

interface ReadinessMeta {
  tone: AttentionTone;
  title: string;
  caption: string;
}

/**
 * Условие не выполнено → строка. `siteOpen` — не условие, а итог (в шапке);
 * `contract` — отдельная строка с кнопкой «Отметить» (`ShopContractRow`).
 */
const READINESS_META: Readonly<Record<Exclude<keyof ShopReadiness, "siteOpen" | "contract">, ReadinessMeta>> = {
  legalProfile: { tone: "danger", title: "Нет реквизитов продавца", caption: "без них сайт открыть нельзя (SB59)" },
  privacyPolicy: { tone: "danger", title: "Не опубликована политика ПДн", caption: "без неё сайт открыть нельзя" },
  orderPipeline: { tone: "danger", title: "Нет воронки заказов", caption: "оформление заказа не работает" },
  returnPipeline: { tone: "warning", title: "Нет воронки возвратов", caption: "возвраты оформить нельзя" },
  paymentIntegration: { tone: "warning", title: "Оплата не подключена", caption: "онлайн-оплата в оформлении скрыта" },
  fiscal: { tone: "warning", title: "Касса не подключена", caption: "чеки не формируются" },
  hasPublishedSite: { tone: "info", title: "Сайт ещё не опубликован", caption: "владелец публикует его в конструкторе" },
};

const READINESS_ORDER = Object.keys(READINESS_META) as (keyof typeof READINESS_META)[];

/** Строки «Требует внимания»: сначала адреса, затем готовность. */
export function shopAttentionItems(shop: ShopCard, base: string, now: Date = new Date()): AttentionItem[] {
  const items: AttentionItem[] = [];
  for (const domain of shop.domains) {
    if (domain.status !== "ACTIVE") continue;
    const meta = checkStatusMeta(domain.lastCheck, now);
    if (meta.tone !== "error" && meta.tone !== "attention") continue;
    items.push({
      key: `domain-${domain.id}`,
      tone: meta.tone === "error" ? "danger" : "warning",
      title: `${meta.label}: ${domain.hostUnicode || domain.host}`,
      to: `${base}/domains`,
    });
  }
  for (const key of READINESS_ORDER) {
    // Поля нет в ответе (старый API) — не тревожим; false — условие не выполнено.
    if (shop.readiness[key] !== false) continue;
    const meta = READINESS_META[key];
    items.push({ key, tone: meta.tone, title: meta.title, caption: meta.caption, to: `${base}/access` });
  }
  return items;
}
